<template>
    <div class="content-wrapper">
        <!-- Page Loader -->
        <div class="vld-parent">
            <loading :active.sync="isLoading" :is-full-page="fullPage"></loading>
        </div>

        <breadcrumb :page="$t('form.program.create')"></breadcrumb>

        <div class="content">
            <div class="container-fluid">

                <b-alert v-model="alert" :variant="alertClass" dismissible fade>{{alertMessage}}</b-alert>

                <div class="card">
                    <form role="form" @submit.prevent="saveProgram()">
                        <div class="card-header bg-light">
                            <div class="card-title">{{$t('form.program.create')}}</div>
                            <button class="btn btn-primary float-right">{{$t('button.save')}}</button>
                        </div>

                        <div class="card-body">

                            <div class="row">
                                <div class="col-sm-4">

                                    <div class="form-group">
                                        <label>{{$t('form.program.programCode')}}<span
                                                class="text-danger">*</span></label>
                                        <v-select :options="programCodes" v-model.trim="$v.program.ProgramCode.$model"
                                            :reduce="programCodes => programCodes">
                                        </v-select>
                                        <div class="invalid-feedback custom-validate" v-if="invalidProgramCode">
                                            {{$t('form.validation.required')}}</div>
                                    </div>

                                    <div class="form-group">
                                        <label>{{$t('form.program.programType')}}<span
                                                class="text-danger">*</span></label>
                                        <v-select :options="programTypes" v-model.trim="$v.program.ProgramType.$model"
                                            :reduce="programTypes => programTypes">
                                        </v-select>
                                        <div class="invalid-feedback custom-validate" v-if="invalidProgramType">
                                            {{$t('form.validation.required')}}</div>
                                    </div>

                                    <div class="form-group">
                                        <label>
                                            {{$t('form.program.programTitle')}}
                                            <span class="text-danger">*</span>
                                        </label>
                                        <input type="text" class="form-control"
                                            v-model.trim="$v.program.ProgramTitle.$model"
                                            :class="{ 'is-invalid': $v.program.ProgramTitle.$error }">
                                        <div class="invalid-feedback" v-if="!$v.program.ProgramTitle.required">
                                            {{$t('form.validation.required')}}</div>
                                    </div>

                                    <div class="form-group">
                                        <label>{{$t('form.program.transcriptTitle')}}</label>
                                        <v-select :options="programTranscriptTitles" v-model.trim="program.TranscriptTitle">
                                        </v-select>
                                    </div>

                                    <div class="form-group">
                                        <label>{{$t('form.program.specialization')}}
                                             <span class="text-danger">*</span>
                                        </label>
                                        <v-select :options="programSpecializations" v-model.trim="program.Specialization" :class="{ 'is-invalid': $v.program.Specialization.$error }">
                                        </v-select>
                                         <div class="invalid-feedback" v-if="!$v.program.Specialization.required">
                                            {{$t('form.validation.required')}}
                                        </div>
                                    </div>

                                    <div class="form-group">
                                        <label>{{$t('form.program.major')}}</label>
                                        <v-select :options="programMajors" v-model.trim="program.Major">
                                        </v-select>
                                    </div>


                                    <div class="form-group">
                                        <label>{{$t('form.program.academicYear')}}
                                            <span class="text-danger">*</span>
                                        </label>
                                        <!-- <input type="date" class="form-control"
                                            v-model.trim="$v.program.AcademicYear.$model"
                                            :class="{ 'is-invalid': $v.program.AcademicYear.$error }"> -->
                                        <select v-model.trim="$v.program.AcademicYear.$model" class="form-control"
                                            :class="{ 'is-invalid': $v.program.AcademicYear.$error }">
                                            <option v-for="(year , index) in programYears" :key="index" :value="year">
                                                {{year}}</option>
                                        </select>
                                        <div class="invalid-feedback" v-if="!$v.program.ProgramTitle.required">
                                            {{$t('form.validation.required')}}</div>
                                    </div>

                                    <div class="form-group" v-if="showFoundationProgramField">
                                        <label>{{ $t('form.program.foundationProgram') }}</label>
                                        <v-select
                                            :options="foundationPrograms"
                                            :get-option-label="p => p.FullTitle"
                                            :reduce="p => p.ID"
                                            v-model="program.FoundationID"
                                        />
                                    </div>

                                    <div class="form-group">
                                        <label>{{$t('form.program.notes')}}</label>
                                        <textarea rows="5" class="form-control" v-model="program.Notes"></textarea>
                                    </div>

                                    <div class="form-group">
                                        <label>{{$t('form.program.gradingScaleType')}}</label>
                                        <v-select label="name" :reduce="t => Number(t.id)" :options="gradingScaleTypes" v-model="program.GradingScaleTypeID"></v-select>
                                        <small class="form-text text-muted">{{$t('form.program.gradingScaleTypeHint')}}</small>
                                    </div>

                                    <div class="form-group">
                                        <label>{{$t('form.student.partnerCode')}}</label>
                                        <v-select label="PartnerCode" :options="partners" v-model="program.PartnerCode"
                                            :reduce="partners => partners.PartnerCode" multiple>
                                        </v-select>
                                    </div>
                                    <hr>

                                    <div class="form-group">
                                        <label>{{$t('form.program.billingCycle')}}</label>
                                        <b-form-select v-model="program.BillingCycle" :options="billingCycles">
                                        </b-form-select>
                                    </div>

                                    <div class="form-group  d-flex justify-content-between">
                                        <label>{{$t('form.program.issueInvoices')}}</label>
                                        <input v-model="program.days" type="number" min=1 max=60 class="form-control col-xs-2">
                                        <label class="mt-2">{{$t('form.program.days')}}</label>
                                    
                                        <b-form-select v-model="program.beforeOrAfter" :options="afterOrBeforeDropdown" class="col-xs-2"></b-form-select>
                                        <label>{{$t('form.program.cycleStartDate')}}</label>
                                    </div>

                                    <div class="form-group">
                                        <label>{{$t('form.program.applicationFee')}}</label>
                                        <b-form-input v-model="program.ApplicationFee"></b-form-input>
                                    </div>

                                    <div class="form-group">
                                        <label>{{$t('form.program.acceptanceFee')}}</label>
                                        <b-form-input v-model="program.AcceptanceFee"></b-form-input>
                                    </div>

                                    <hr>

                                    <template v-if="program.BillingCycle=='term'">
                                        <div v-for="(semester, index) in semesters"
                                        :key="index">
                                        <div class="row">
                                            <label>{{$t('form.program.semTerm')}} {{semester}}</label>
                                        </div>
                                        <form>
                                        <div class="form-row">
                                            <div class="form-group col-md-4">
                                            <label :for="'semesterFee'.semester">{{$t('form.program.fee')}}</label>
                                            <b-form-input v-model="program.TermFee[semester - 1]" :id="'semesterFee'.semester">
                                            </b-form-input>
                                        </div>
                                            <div class="form-group col-md-4">
                                            <label :for="'semesterFrom'.semester">{{$t('form.program.fromSemDate')}}</label>
                                            <date-picker :lang="lang" class="date-picker"
                                                v-if="index == semesters.length-1"
                                                v-model="semesterDates.semesterFrom[semester-1]"
                                                valueType="format" :format="dateFormat"
                                                :append-to-body="false">
                                            </date-picker>
                                            <date-picker  class="date-picker"
                                                :lang="lang"
                                                v-else
                                                v-model="semesterDates.semesterFrom[semester-1]"
                                                valueType="format" :format="dateFormat"
                                                :popup-style="{ top: '100%'}"
                                                :append-to-body="false">
                                            </date-picker>
                                            </div>
                                            <div class="form-group col-md-4">
                                            <label :for="'semesterTo'.semester">{{$t('form.program.toSemDate')}}</label>
                                            <date-picker class="date-picker"
                                                :lang="lang"
                                                v-if="index == semesters.length-1"
                                                v-model="semesterDates.semesterTo[semester-1]"
                                                valueType="format" :format="dateFormat"
                                                :append-to-body="false">
                                            </date-picker>
                                            <date-picker class="date-picker"
                                                v-else
                                                :lang="lang"
                                                v-model="semesterDates.semesterTo[semester-1]"
                                                valueType="format" :format="dateFormat"
                                                :popup-style="{ top: '100%'}"
                                                :append-to-body="false">
                                            </date-picker>
                                            </div>
                                            </div>
                                            </form>
                                        </div>
                                        </template>

                                    <div class="d-flex justify-content-between">
                                        <label>{{$t('form.program.totalCost')}}</label>
                                        <p>{{totalFee | curreny(decimalDigits, decimalPoint, thousandSeparator)}}
                                        </p>
                                    </div>
                                </div>

                                <div class="col-sm-8">
                                    <div class="form-group">
                                        <label>{{$t('form.program.courses')}}</label>
                                        <div class="input-group">
                                            <input type="text" class="form-control" v-model="courseTitle"
                                                :placeholder="$t('form.program.searchCourse')" @click="filterCourses"
                                                autocomplete="off">
                                            <div class="input-group-append" style="cursor: pointer">
                                                <span class="input-group-text" @click="clear()">
                                                    <i class="fa fa-times"></i>
                                                </span>
                                            </div>
                                        </div>
                                        <ul class="list-group m-0">
                                            <li class="list-group-item" v-for="(result,index) in courses"
                                                v-bind:key="index" @click="addCourse(result)" style="cursor: pointer">
                                                {{ result.FullTitle}}
                                            </li>
                                        </ul>
                                    </div>
                                    <div class="table-responsive p-0">
                                        <table class="table table-bordered">
                                            <thead>
                                                <tr>
                                                    <th>{{$t('form.program.course')}} </th>
                                                    <th>{{$t('form.program.sem')}}</th>
                                                    <th>{{$t('form.program.programYear')}}</th>
                                                    <th>{{$t('form.program.fromCourse')}}</th>
                                                    <th>{{$t('form.program.toCourse')}}</th>
                                                    <th class="text-right">{{$t('form.program.action')}}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr v-for="(course, index) in $v.programCourses.$each.$iter"
                                                    :key="index">
                                                    <td>{{course.$model.CourseCode}} - {{course.$model.CourseTitle}}
                                                    </td>
                                                    <td><input type="number"
                                                        v-model="course.$model.Semester"
                                                        class="form-control" @change="fetchSemesters()"></td>
                                                    <td style="width: 100px;">
                                                        <input type="number" v-model="course.ProgramYear.$model"
                                                            class="form-control"
                                                            :class="{ 'is-invalid': course.ProgramYear.$error }">
                                                        <div class="invalid-feedback"
                                                            v-if="!course.ProgramYear.between">
                                                            {{$t('form.validation.between', {num1: course.ProgramYear.$params.between.min, num2: course.ProgramYear.$params.between.max})}}
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <date-picker :lang="lang" class="date-picker"
                                                            v-model="course.$model.CourseFrom" valueType="format"
                                                            :format="dateFormat" :popup-style="{ top: '100%', left: 0}"
                                                            :append-to-body="false">
                                                        </date-picker>
                                                    </td>
                                                    <td>
                                                        <date-picker :lang="lang" class="date-picker"
                                                            v-model="course.$model.CourseTo" valueType="format"
                                                            :format="dateFormat" :popup-style="{ top: '100%', left: 0}"
                                                            :append-to-body="false">
                                                        </date-picker>
                                                    </td>
                                                    <td class="text-right">
                                                        <a class="btn btn-danger text-white btn-sm"
                                                            @click="removeCourse(index)">
                                                            <i class="fa fa-times" aria-hidden="true"></i>
                                                        </a>
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
    import {
        required,
        minLength,
        maxLength,
        alpha,
        between
    } from "vuelidate/lib/validators";

    export default {
        props: ['permissions', 'editFormInterval', 'datetimeFormat', 'applicationFee', 'dateFormat', 'decimalDigits',
            'decimalPoint', 'thousandSeparator'
        ],

        data() {
            return {
                foundationAllowedCodes: [],
                foundationPrograms: [],
                alert: false,
                alertClass: '',
                alertMessage: '',

                isLoading: false,
                fullPage: true,

                request_source: '',
                invalidProgramCode: false,
                invalidProgramType: false,
                lang: {
                    formatLocale: {
                        firstDayOfWeek: 1,
                    },
                },
                gradingScaleTypes: [],
                program: {
                    ProgramTitle: null,
                    TranscriptTitle: null,
                    ProgramCode: null,
                    ProgramType: null,
                    AcademicYear: null,
                    FoundationID: null,
                    GradingScaleTypeID: null,
                    Specialization: null,
                    Major: null,
                    Notes: null,
                    BillingCycle: 'inherit',
                    beforeOrAfter:'before',
                    ApplicationFee: 150,
                    AcceptanceFee: null,
                    TermFee: [],
                    PartnerCode: [],
                    days:null,
                    TS: null
                },
                billingCycles: [
                    {
                        text: this.$t('form.program.defaultProgramTypeBillingCycle'),
                        value: 'inherit'
                    },
                    {
                        text: this.$t('form.program.byCourse'),
                        value: 'course'
                    },
                    {
                        text: this.$t('form.program.byTerm'),
                        value: 'term'
                    },
                ],
                afterOrBeforeDropdown:[{
                text: this.$t('form.program.before'),
                        value: 'before'
                },{
                text: this.$t('form.program.after'),
                        value: 'after'
                }],
                programCodes: [],
                programTypes: [],
                programYears: [],
                programSpecializations: [],
                programTranscriptTitles: [],
                programMajors: [],
                programCourses: [],
                partners: [],

                courses: [],
                courseTitle: '',
                semesters:[],

                semesterDates:{
                   semesterFrom:[],
                   semesterTo:[] 
                },
            }
        },
        validations: {
            program: {
                ProgramCode: {
                    required
                },
                ProgramType: {
                    required
                },
                ProgramTitle: {
                    required,
                    maxLength: maxLength(100)
                },
                AcademicYear: {
                    required
                },
                Specialization:{
                    required,
                    maxLength: maxLength(200)
                }
            },
            programCourses: {
                $each: {
                    ProgramYear: {
                        between: between(1, 10)
                    }
                }
            }
        },

        mounted() {
            if (!this.permissions.createPrograms) {
                this.$router.push("/");
                return;
            }

            this.initialData();
        },
        computed:{
            showFoundationProgramField() {
                return this.foundationAllowedCodes.includes(this.program.ProgramCode);
            },
            defaultApplicationFee() {
                return this.applicationFee; // fetch from config later
            },
            totalFee() {
                let total = 0;
                let application_fee = parseFloat(this.program.ApplicationFee);
                let acceptance_fee = parseFloat(this.program.AcceptanceFee);

                // Semesters/Terms Sum
                if (this.program.BillingCycle == 'term') {
                    total = this.program.TermFee.reduce((a, b) => {
                        if (b) return parseFloat(a) + parseFloat(b)

                        return parseFloat(a)
                    }, 0);
                }

                // Courses Sum
                else if (this.program.BillingCycle == 'course') {
                    total = this.programCourses.reduce((a, b) => {
                        if (b.Fee) return parseFloat(a) + parseFloat(b.Fee)

                        return parseFloat(a)
                    }, 0);
                }

                // Application Fee Add
                if (application_fee) total += application_fee;

                // Administration Fee Add
                if (acceptance_fee) total += acceptance_fee;

                return total.toFixed(2);
            }
        },
        watch: {
            courseTitle(value) {
                if (value == '') {
                    this.courses = [];
                    return;
                };

                this.filterCourses();
            }
        },

        methods: {
            fetchSemesters() {
                let semesters = [];

                this.programCourses.forEach(c => {
                    let s = parseInt(c.Semester);
                    if (s) semesters.push(s);
                });

                this.semesters = [...new Set(semesters)].sort();
            },

            initialData() {
                this.isLoading = true;

                axios
                    .get('/api/programs/initial-data')
                    .then(({
                        data
                    }) => {
                        this.programCodes = data.program_codes;
                        this.programTypes = data.program_types;
                        this.programYears = data.program_years;
                        this.programSpecializations  = data.program_specializations;
                        this.programTranscriptTitles  = data.program_transcript_titles;

                        this.gradingScaleTypes = data.grading_scale_types;

                        this.programMajors  = data.program_majors;

                        this.partners = data.partners;

                        this.foundationAllowedCodes = data.foundation_program_codes;
                        this.foundationPrograms = data.foundation_programs.map(p => {
                            return {
                                ...p,
                                FullTitle: `${p.ProgramTitle}, ${p.ProgramCode}, ${p.ProgramType}, ${new Date(p.AcademicYear).getFullYear()}`
                            };
                        });

                        this.isLoading = false;
                    })
                    .catch(error => this.isLoading = false);
            },

            filterCourses() {

                let source = axios.CancelToken.source();

                if (this.request_source != '')
                    this.request_source.cancel('Operation canceled by the user.');
                    console.clear();

                this.request_source = source;

                axios
                    .get("/api/courses/", {
                        params: {
                            codeOrTitle: this.courseTitle
                        },
                        cancelToken: this.request_source.token
                    })
                    .then(response => {
                        this.courses = response.data.data.data;
                        this.courses.map(course => {
                            course.FullTitle = `${course.ID} - ${course.CourseCode} - ${course.CourseTitle} - ${course.ECTSCredits} ECTS - ${course.SBSCredits ? course.SBSCredits + ' SBS' : '0 SBS'}`;
                            
                        });
                    })
                    .catch(error => console.log(error));
            },

            clear() {
                this.courses = [];
                this.courseTitle = '';
            },

            addCourse({
                ID,
                CourseTitle,
                CourseCode
            }) {
                this.programCourses.push({
                    ID,
                    CourseTitle,
                    CourseCode,
                    CourseFrom: null,
                    CourseTo: null,
                    Fee: null,
                    Semester: null,
                    SemesterFrom: null,
                    SemesterTo: null,
                    ProgramYear: null,
                });

                this.clear();
            },

            removeCourse(index) {
                this.programCourses.splice(index, 1);
                this.fetchSemesters();
            },

            saveProgram() {
                this.$v.$touch();

                this.invalidProgramCode = this.program.ProgramCode == null ? true : false;
                this.invalidProgramType = this.program.ProgramType == null ? true : false;

                if (this.$v.$invalid) {
                     let errorMsg = '';
                      if(this.$v.program.ProgramCode.$error){
                            errorMsg = this.$t('form.program.programCode');
                      }

                      if(this.$v.program.ProgramType.$error){
                            errorMsg = errorMsg ? errorMsg +", "+this.$t('form.program.programType'): this.$t('form.program.programType');
                      }

                    if(this.$v.program.ProgramTitle.$error){
                            errorMsg = errorMsg ? errorMsg +","+ this.$t('form.program.programTitle'): this.$t('form.program.programTitle');
                      }

                    if(this.$v.program.AcademicYear.$error){
                            errorMsg = errorMsg ? errorMsg +" ,"+this.$t('form.program.academicYear'): this.$t('form.program.academicYear');
                      }

                    if(this.$v.program.Specialization.$error){
                            errorMsg = errorMsg ? errorMsg +","+this.$t('form.program.specialization'): this.$t('form.program.specialization');
                      }

                  if(this.$v.programCourses.$each.$error){
                             errorMsg = errorMsg ? errorMsg +", "+this.$t('form.program.programYear'): this.$t('form.program.programYear');
                      }


                    this.showAlert(this.$t('form.validation.mandatoryFieldsError', {errors:errorMsg}), 'danger');
                    return; 
                }

                this.isLoading = true;

                let formData = new FormData();

                formData.append("program", JSON.stringify(this.program));


                this.programCourses.forEach(course => {
                    course.SemesterFrom = course.Semester ? this.semesterDates['semesterFrom'][course.Semester - 1] : null;
                    course.SemesterTo = course.Semester ?  this.semesterDates['semesterTo'][course.Semester - 1] : null;
                    formData.append("courses[]", JSON.stringify(course))
                    });


                axios
                    .post('/api/programs', formData)
                    .then(response => {
                        this.isLoading = false;

                        if (response.data.message) {
                            this.showAlert(response.data.message);

                            setTimeout(() => {
                                this.$router.push({
                                    name: 'ProgramIndex'
                                });
                            }, 2000);
                        }
                    })
                    .catch(error => {
                        this.isLoading = false;

                        if (error.response.data) {
                            this.showAlert(error.response.data.message, 'danger');
                        }
                    });
            },

            showAlert(msg, cls = 'success') {
                this.alertMessage = msg;
                this.alertClass = cls;
                this.alert = true;
            }
        }
    };

</script>

<style scoped>
    .custom-validate {
        display: block !important;
    }

</style>
